// WaiverFormModal.jsx
import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { GlobalStyles } from "../../constants/styles";
import { alphaFromHex } from "../../util/colors";
import useTranslation from "../../i18n/useTranslation";

/**
 * WaiverFormModal
 * Props:
 * - waiver: waiver object to edit
 * - onClose: () => void
 * - onSave: (input) => Promise<void> | void
 */
export default function WaiverFormModal({ waiver, onClose, onSave, colors }) {
    const scopeId = "kbola-waiver-edit-modal";
    const { t, supportedLanguages } = useTranslation();

    const initialLanguage = (waiver?.language || "").toString().toLowerCase();

    const [title, setTitle] = useState(waiver?.title || "");
    const [waiverLanguage, setWaiverLanguage] = useState(
        supportedLanguages.includes(initialLanguage) ? initialLanguage : "en"
    );
    const [waiverType, setWaiverType] = useState(waiver?.waiverType || "TEXT");
    const [content, setContent] = useState(waiver?.content || "");
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        const normalizedLanguage = (waiver?.language || "").toString().toLowerCase();
        setTitle(waiver?.title || "");
        setWaiverLanguage(supportedLanguages.includes(normalizedLanguage) ? normalizedLanguage : "en");
        setWaiverType(waiver?.waiverType || "TEXT");
        setContent(waiver?.content || "");
        setFile(null);
    }, [waiver, supportedLanguages]);

    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = prev || "";
        };
    }, []);

    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key === "Escape") onClose?.();
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [onClose]);

    const ui = useMemo(() => {
        return {
            backdrop: {
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.55)",
                zIndex: 2050,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 16,
            },
            dialog: {
                width: "100%",
                maxWidth: 880,
            },
            content: {
                borderRadius: 18,
                overflow: "hidden",
                border: "1px solid rgba(0,0,0,0.08)",
                background: "#fff",
                boxShadow: "0 18px 44px rgba(0,0,0,0.18)",
            },
            header: {
                padding: "12px 14px",
                background: `linear-gradient(
                    135deg,
                    ${alphaFromHex(colors.primary, 0.50)},
                    ${alphaFromHex(colors.primary, 0.00)}
                )`,
                borderBottom: "1px solid rgba(0,0,0,0.06)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                position: "sticky",
                top: 0,
                zIndex: 2,
            },
            titleWrap: { display: "flex", alignItems: "center", gap: 10, minWidth: 0 },
            badge: {
                fontSize: 11.5,
                fontWeight: 900,
                letterSpacing: "0.03em",
                padding: "6px 10px",
                borderRadius: 999,
                background: colors.primary,
                border: "1px solid rgba(0,0,0,0.10)",
                color: colors.textOnPrimary,
                whiteSpace: "nowrap",
            },
            title: {
                margin: 0,
                fontWeight: 900,
                fontSize: 14,
                color: colors.textOnPrimary,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
            },
            sub: { margin: 0, fontSize: 12.5, fontWeight: 700, color: colors.textOnPrimary },

            closeBtn: {
                width: 38,
                height: 38,
                borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.10)",
                background: colors.primary,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                lineHeight: 1,
                color: colors.textOnPrimary,
            },

            body: {
                padding: 14,
                background: "#fff",
                maxHeight: "calc(100vh - 220px)",
                overflowY: "auto",
            },

            footer: {
                padding: "12px 14px",
                borderTop: "1px solid rgba(0,0,0,0.06)",
                display: "flex",
                justifyContent: "space-between",
                gap: 10,
                background: "#fff",
            },

            btn: {
                borderRadius: 12,
                padding: "10px 12px",
                fontWeight: 900,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                border: "1px solid rgba(0,0,0,0.12)",
                background: "#fff",
                color: "#14151a",
                cursor: "pointer",
                lineHeight: 1,
            },
            btnPrimary: {
                background: GlobalStyles.colors.canary,
                border: "1px solid rgba(0,0,0,0.10)",
                color: GlobalStyles.colors.woodsmoke,
            },

            label: {
                fontWeight: 900,
                fontSize: 12.5,
                color: "rgba(20,21,26,0.75)",
                marginBottom: 6,
            },
            helper: {
                marginTop: 6,
                fontSize: 12.25,
                fontWeight: 700,
                color: "rgba(20,21,26,0.55)",
            },

            section: {
                borderRadius: 14,
                border: "1px solid rgba(0,0,0,0.06)",
                background: "rgba(0,0,0,0.015)",
                padding: 14,
            },
            fileWrap: {
                borderRadius: 14,
                border: "1px dashed rgba(0,0,0,0.18)",
                background: "rgba(0,0,0,0.015)",
                padding: 12,
            },
            fileRow: {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
                flexWrap: "wrap",
            },
            fileName: {
                fontWeight: 900,
                color: "#14151a",
                maxWidth: "100%",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
            },
            fileBtn: {
                borderRadius: 12,
                padding: "10px 12px",
                fontWeight: 900,
                border: "1px solid rgba(0,0,0,0.12)",
                background: "#fff",
                color: "#14151a",
                cursor: "pointer",
                lineHeight: 1,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 0,
            },
            hiddenFile: { position: "absolute", left: -9999, opacity: 0 },
        };
    }, [colors.primary, colors.textOnPrimary]);

    if (!waiver) return null;

    const handleBackdropClick = (e) => {
        if (e.target?.id === `${scopeId}-backdrop`) onClose?.();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setUploading(true);
        try {
            const input = {
                title: title?.trim(),
                language: waiverLanguage,
                waiverType,
                content: waiverType === "TEXT" ? content : null,
                file: waiverType === "DOCUMENT" ? file || null : null,
            };
            await onSave?.(input);
        } finally {
            setUploading(false);
        }
    };

    const isDoc = waiverType === "DOCUMENT";
    const hasDocumentSource = !!(waiver?.documentUrl || file);
    const canSave = title.trim().length > 0 && (isDoc ? hasDocumentSource : true) && !uploading;
    const fileInputId = `${scopeId}-file`;
    const languageLabels = { en: "English", es: "Español", fr: "Français" };

    return createPortal(
        <div
            id={`${scopeId}-backdrop`}
            style={ui.backdrop}
            onMouseDown={handleBackdropClick}
            role="dialog"
            aria-modal="true"
        >
            <div style={ui.dialog}>
                <div style={ui.content} onMouseDown={(e) => e.stopPropagation()}>
                    <div style={ui.header}>
                        <div style={ui.titleWrap}>
                            <span style={ui.badge}>{t("waiverEditModal.badge")}</span>
                            <div style={{ minWidth: 0 }}>
                                <h5 style={ui.title}>{t("waiverEditModal.title")}</h5>
                                <p style={ui.sub}>{t("waiverEditModal.subtitle")}</p>
                            </div>
                        </div>

                        <button
                            type="button"
                            style={ui.closeBtn}
                            onClick={onClose}
                            aria-label={t("waiverEditModal.close")}
                            title={t("waiverEditModal.close")}
                        >
                            <i className="fas fa-times" />
                        </button>
                    </div>

                    <form onSubmit={handleSubmit}>
                        <div style={ui.body} id={`${scopeId}-body`}>
                            <div style={ui.section}>
                                <div className="row">
                                    <div className="col-12 mb-3">
                                        <div style={ui.label}>{t("waiverEditModal.titleLabel")}</div>
                                        <input
                                            className="form-control"
                                            value={title}
                                            onChange={(e) => setTitle(e.target.value)}
                                            placeholder={t("waiverEditModal.titlePlaceholder")}
                                            required
                                        />
                                        <div style={ui.helper}>{t("waiverEditModal.titleHelper")}</div>
                                    </div>

                                    <div className="col-md-6 mb-3">
                                        <div style={ui.label}>{t("waiverEditModal.language")}</div>
                                        <select
                                            className="form-control"
                                            value={waiverLanguage}
                                            onChange={(e) => setWaiverLanguage(e.target.value)}
                                        >
                                            {supportedLanguages.map((langCode) => (
                                                <option key={langCode} value={langCode}>
                                                    {languageLabels[langCode] || langCode.toUpperCase()}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="col-md-6 mb-3">
                                        <div style={ui.label}>{t("waiverEditModal.waiverType")}</div>
                                        <select
                                            className="form-control"
                                            value={waiverType}
                                            onChange={(e) => {
                                                const next = e.target.value;
                                                setWaiverType(next);
                                                if (next === "TEXT") setFile(null);
                                                if (next === "DOCUMENT") setContent("");
                                            }}
                                        >
                                            <option value="TEXT">{t("waiverEditModal.text")}</option>
                                            <option value="DOCUMENT">{t("waiverEditModal.document")}</option>
                                        </select>
                                        <div style={ui.helper}>{t("waiverEditModal.waiverTypeHelper")}</div>
                                    </div>

                                    <div className="col-md-6 mb-3">
                                        <div style={ui.label}>{t("waiverEditModal.createdBy")}</div>
                                        <input
                                            className="form-control"
                                            value={waiver?.createdBy?.email || "—"}
                                            readOnly
                                            style={{ background: "rgba(0,0,0,0.03)" }}
                                        />
                                        <div style={ui.helper}>{t("waiverEditModal.readOnly")}</div>
                                    </div>

                                    {waiverType === "TEXT" && (
                                        <div className="col-12">
                                            <div style={ui.label}>{t("waiverEditModal.content")}</div>
                                            <textarea
                                                className="form-control"
                                                rows={10}
                                                value={content}
                                                onChange={(e) => setContent(e.target.value)}
                                                placeholder={t("waiverEditModal.contentPlaceholder")}
                                            />
                                            <div style={ui.helper}>{t("waiverEditModal.contentHelper")}</div>
                                        </div>
                                    )}

                                    {waiverType === "DOCUMENT" && (
                                        <div className="col-12">
                                            <div style={ui.label}>{t("waiverEditModal.pdfDocument")}</div>

                                            <div style={ui.fileWrap}>
                                                <div style={ui.fileRow}>
                                                    <div style={{ minWidth: 0, flex: "1 1 auto" }}>
                                                        <div style={ui.fileName} title={file?.name || ""}>
                                                            {file ? file.name : t("waiverEditModal.noFileSelected")}
                                                        </div>
                                                        <div style={ui.helper}>{t("waiverEditModal.pdfHelper")}</div>

                                                        {waiver?.documentUrl ? (
                                                            <a
                                                                href={waiver.documentUrl}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                style={{ ...ui.helper, display: "inline-block", marginTop: 8 }}
                                                            >
                                                                {t("waiverEditModal.viewCurrentDocument")}
                                                            </a>
                                                        ) : null}
                                                    </div>

                                                    <div style={{ flex: "0 0 auto" }}>
                                                        <label htmlFor={fileInputId} style={ui.fileBtn}>
                                                            <i className="fas fa-upload" />
                                                            {t("waiverEditModal.choosePdf")}
                                                        </label>
                                                        <input
                                                            id={fileInputId}
                                                            type="file"
                                                            style={ui.hiddenFile}
                                                            accept="application/pdf"
                                                            onChange={(e) => setFile(e.target.files?.[0] || null)}
                                                            disabled={uploading}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div style={ui.footer}>
                            <button type="button" style={ui.btn} onClick={onClose}>
                                <i className="fas fa-times" />
                                {t("waiverEditModal.cancel")}
                            </button>

                            <button type="submit" style={{ ...ui.btn, ...ui.btnPrimary }} disabled={!canSave}>
                                <i className="fas fa-save" />
                                {uploading ? t("waiverEditModal.saving") : t("waiverEditModal.saveChanges")}
                            </button>
                        </div>
                    </form>

                    <style>{`
            #${scopeId}-backdrop .form-control:focus,
            #${scopeId}-backdrop .btn:focus,
            #${scopeId}-backdrop button:focus,
            #${scopeId}-backdrop select:focus,
            #${scopeId}-backdrop textarea:focus{
              outline: none !important;
              box-shadow: 0 0 0 0.2rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.18) !important;
              border-color: rgba(0,0,0,0.15) !important;
            }

            #${scopeId}-backdrop .form-control{
              border-radius: 12px;
              border: 1px solid rgba(0,0,0,0.12);
              font-weight: 800;
              color: rgba(20,21,26,0.90);
            }

            #${scopeId}-backdrop textarea.form-control{
              font-weight: 700;
              line-height: 1.4;
            }

            #${scopeId}-body{
              scrollbar-width: thin;
              scrollbar-color: rgba(0,0,0,0.25) transparent;
            }
            #${scopeId}-body::-webkit-scrollbar{
              width: 10px;
            }
            #${scopeId}-body::-webkit-scrollbar-thumb{
              background: rgba(0,0,0,0.18);
              border-radius: 999px;
              border: 3px solid rgba(255,255,255,0.6);
            }

            #${scopeId}-backdrop i.fas,
            #${scopeId}-backdrop i.far,
            #${scopeId}-backdrop i.fal,
            #${scopeId}-backdrop i.fa,
            #${scopeId}-backdrop i[class^="fa-"],
            #${scopeId}-backdrop i[class*=" fa-"] {
              font-family: "Font Awesome 5 Free" !important;
              font-weight: 900;
            }
            #${scopeId}-backdrop i.fab {
              font-family: "Font Awesome 5 Brands" !important;
              font-weight: 400;
            }
          `}</style>
                </div>
            </div>
        </div>,
        document.body
    );
}
